import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();

  const productQuery = useQuery({
    queryKey: ['product', id],
    queryFn: () => inventoryApi.getProduct(id!),
    enabled: !!id,
  });
  const movementsQuery = useQuery({
    queryKey: ['product-movements', id],
    queryFn: () => inventoryApi.getMovements(id!, 50),
    enabled: !!id,
  });

  if (productQuery.isLoading) return <LoadingState />;
  if (productQuery.error) return <ErrorState error={productQuery.error} />;

  const p = productQuery.data;
  if (!p) return null;

  return (
    <div className="p-6">
      <Link to="/inventory/products" className="text-sm text-sage-600 hover:underline">← Back to products</Link>
      <PageHeader title={p.name} subtitle={`SKU ${p.sku}${p.barcode ? ' · ' + p.barcode : ''}`} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="card p-4">
          <div className="text-sm text-slate-500">Base price</div>
          <div className="text-2xl font-bold">{fmtCurrency(parseFloat(p.base_price))}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-slate-500">Cost (avg)</div>
          <div className="text-2xl font-bold">{fmtCurrency(parseFloat(p.cost_price))}</div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-slate-500">Valuation method</div>
          <div className="text-2xl font-bold">{p.valuation_method}</div>
        </div>
      </div>

      <h2 className="font-semibold mb-2">Stock by warehouse</h2>
      <div className="card overflow-x-auto mb-6">
        <table className="table">
          <thead>
            <tr>
              <th>Warehouse</th>
              <th className="text-right">Physical</th>
              <th className="text-right">Reserved</th>
              <th className="text-right">WAC cost</th>
            </tr>
          </thead>
          <tbody>
            {(p.stock ?? []).length === 0 ? (
              <tr><td colSpan={4}><EmptyState message="No stock records." /></td></tr>
            ) : p.stock.map((s: any, idx: number) => (
              <tr key={`${s.warehouse_id}-${s.variant_id ?? idx}`}>
                <td>{s.name} <span className="text-slate-500 text-xs">({s.code})</span></td>
                <td className="text-right">{parseFloat(s.physical_qty).toFixed(2)}</td>
                <td className="text-right">{parseFloat(s.reserved_qty).toFixed(2)}</td>
                <td className="text-right">{fmtCurrency(parseFloat(s.wac_unit_cost))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-semibold mb-2">Recent stock movements</h2>
      <div className="card overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Type</th>
              <th>Warehouse</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit cost</th>
              <th>Reference</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {movementsQuery.isLoading ? (
              <tr><td colSpan={7} className="text-center py-4 text-slate-400">Loading…</td></tr>
            ) : !movementsQuery.data || movementsQuery.data.length === 0 ? (
              <tr><td colSpan={7}><EmptyState message="No movements." /></td></tr>
            ) : movementsQuery.data.map((m: any) => (
              <tr key={m.id}>
                <td className="text-xs">{new Date(m.created_at).toLocaleString()}</td>
                <td>{m.movement_type}</td>
                <td>{m.warehouse_code}</td>
                <td className={`text-right font-mono ${parseFloat(m.quantity) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {parseFloat(m.quantity) >= 0 ? '+' : ''}{parseFloat(m.quantity).toFixed(2)}
                </td>
                <td className="text-right">${m.unit_cost ? parseFloat(m.unit_cost).toFixed(4) : '—'}</td>
                <td className="text-xs text-slate-500 font-mono">{m.reference_type}:{m.reference_id?.slice(0, 8)}</td>
                <td className="text-xs">{m.created_by_name ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
